import React from "react";
import { Typography, makeStyles } from "@material-ui/core";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";

import { i18n } from "../../translate/i18n";
import { buildTemplatePreviewParts } from "../../utils/templateVariables";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
  },
  previewLabel: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  phoneFrame: {
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.08)",
    background: "#0b141a",
  },
  phoneHeader: {
    background: "#1f2c34",
    color: "#e9edef",
    padding: theme.spacing(1.25, 1.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: "0.9rem",
    fontWeight: 500,
  },
  phoneAvatar: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "#54656f",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.8rem",
  },
  phoneBody: {
    minHeight: 220,
    padding: theme.spacing(2, 1.5, 2.5),
    backgroundColor: "#0b141a",
    backgroundImage:
      "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.03) 0, transparent 40%), radial-gradient(circle at 80% 0%, rgba(255,255,255,0.03) 0, transparent 35%)",
  },
  todayPill: {
    margin: "0 auto 12px",
    width: "fit-content",
    padding: "4px 10px",
    borderRadius: 8,
    background: "rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.75)",
    fontSize: "0.72rem",
  },
  messageBubble: {
    marginLeft: "auto",
    maxWidth: "88%",
    background: "#005c4b",
    color: "#e9edef",
    borderRadius: "12px 12px 2px 12px",
    padding: theme.spacing(1.25, 1.5),
    boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  bubbleHeader: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.75),
  },
  bubbleFooter: {
    marginTop: theme.spacing(1),
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.65)",
  },
  bubbleButtons: {
    marginTop: theme.spacing(1),
    borderTop: "1px solid rgba(255,255,255,0.12)",
    paddingTop: theme.spacing(0.75),
  },
  bubbleButton: {
    textAlign: "center",
    color: "#53bdeb",
    fontSize: "0.82rem",
    padding: "4px 0",
  },
  bubbleMedia: {
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    overflow: "hidden",
    background: "rgba(255,255,255,0.08)",
    fontSize: "0.78rem",
    color: "rgba(255,255,255,0.75)",
  },
  bubbleMediaImage: {
    display: "block",
    width: "100%",
    maxHeight: 140,
    objectFit: "cover",
  },
  bubbleMediaLabel: {
    padding: theme.spacing(1),
    textAlign: "center",
  },
  bubbleTime: {
    marginTop: theme.spacing(0.75),
    textAlign: "right",
    fontSize: "0.68rem",
    color: "rgba(255,255,255,0.55)",
  },
  hint: {
    marginTop: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontSize: "0.82rem",
  },
  emptyPreview: {
    color: "rgba(255,255,255,0.55)",
    fontSize: "0.85rem",
  },
  authInfo: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    background: "rgba(255,255,255,0.04)",
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    lineHeight: 1.5,
  },
}));

function formatPreviewTime(date = new Date()) {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function renderPreviewParts(parts, classes) {
  return (
    <div className={classes.messageBubble}>
      {parts.map((part, index) => {
        if (part.kind === "header") {
          return (
            <div key={`header-${index}`} className={classes.bubbleHeader}>
              {part.text}
            </div>
          );
        }
        if (part.kind === "media") {
          return (
            <div key={`media-${index}`} className={classes.bubbleMedia}>
              {part.previewUrl ? (
                <img
                  src={part.previewUrl}
                  alt={part.label}
                  className={classes.bubbleMediaImage}
                />
              ) : (
                <div className={classes.bubbleMediaLabel}>[{part.label}]</div>
              )}
            </div>
          );
        }
        if (part.kind === "body") {
          return <div key={`body-${index}`}>{part.text}</div>;
        }
        if (part.kind === "footer") {
          return (
            <div key={`footer-${index}`} className={classes.bubbleFooter}>
              {part.text}
            </div>
          );
        }
        if (part.kind === "buttons") {
          return (
            <div key={`buttons-${index}`} className={classes.bubbleButtons}>
              {part.buttons.map((button, buttonIndex) => (
                <div key={`btn-${buttonIndex}`} className={classes.bubbleButton}>
                  {button.text}
                </div>
              ))}
            </div>
          );
        }
        return null;
      })}
      <div className={classes.bubbleTime}>{formatPreviewTime()}</div>
    </div>
  );
}

function WhatsappTemplatePreview({
  components,
  businessName,
  emptyText,
  authPreviewParts,
  hintText,
  variableValues = {},
}) {
  const classes = useStyles();
  const previewParts =
    authPreviewParts ?? buildTemplatePreviewParts(components, variableValues);
  const hasPreview = previewParts.some(
    (part) =>
      (part.kind === "body" && part.text) ||
      (part.kind === "header" && part.text) ||
      part.kind === "buttons" ||
      part.kind === "media",
  );

  return (
    <div className={classes.root}>
      <div className={classes.previewLabel}>
        <VisibilityOutlinedIcon style={{ fontSize: 16 }} />
        {i18n.t("whatsappTemplates.picker.previewTitle")}
      </div>
      <div className={classes.phoneFrame}>
        <div className={classes.phoneHeader}>
          <div className={classes.phoneAvatar}>W</div>
          <span>{businessName || i18n.t("whatsappTemplates.picker.businessName")}</span>
        </div>
        <div className={classes.phoneBody}>
          <div className={classes.todayPill}>{i18n.t("whatsappTemplates.picker.today")}</div>
          {hasPreview ? (
            renderPreviewParts(previewParts, classes)
          ) : (
            <Typography className={classes.emptyPreview}>
              {emptyText || i18n.t("whatsappTemplates.picker.previewEmpty")}
            </Typography>
          )}
        </div>
      </div>
      {authPreviewParts ? (
        <Typography className={classes.authInfo}>
          {i18n.t("whatsappTemplates.dialog.authPreviewNote")}
        </Typography>
      ) : null}
      {hintText ? <Typography className={classes.hint}>{hintText}</Typography> : null}
    </div>
  );
}

export default WhatsappTemplatePreview;
